import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CaretRight } from '@phosphor-icons/react';

export interface Crumb {
  label: string;
  href: string;
}

// Route segment → display name mapping
const SEGMENT_LABELS: Record<string, string> = {
  health: 'Health',
  goals: 'Goals',
  symptoms: 'Symptoms',
  cycle: 'Cycle',
  sleep: 'Sleep',
  activity: 'Activity',
  nutrition: 'Nutrition',
  mental: 'Mental Wellness',
  intimate: 'Intimate Health',
  vitals: 'Vitals & Labs',
  appointments: 'Appointments',
  treatments: 'Treatments',
  community: 'Community',
  new: 'New Post',
  'my-posts': 'My Posts',
  post: 'Post',
  research: 'Library',
  topics: 'Topics',
  learning: 'Learning Path',
  advocacy: 'Advocacy',
  'letter-generator': 'Letter Generator',
  'communication-guides': 'Guides',
  trials: 'Clinical Trials',
  chat: 'Chat',
  settings: 'Settings',
  'your-data': 'Your Data',
  privacy: 'Privacy',
  terms: 'Terms',
  trust: 'Trust',
};

interface BreadcrumbsProps {
  /** Override the auto-generated trail. If provided, these are used directly. */
  crumbs?: Crumb[];
  /** Label for the current (last) page. If omitted, derived from the URL. */
  current?: string;
}

/**
 * Standardized breadcrumb navigation.
 *
 * Usage:
 *   <Breadcrumbs current="Bladder Control Goal" />
 *   <Breadcrumbs crumbs={[{ label: 'Health', href: '/health' }, { label: 'Goals', href: '/health/goals' }]} current="Bladder Control" />
 *
 * The back arrow always uses browser history so users return to where they came from.
 */
export default function Breadcrumbs({ crumbs, current }: BreadcrumbsProps) {
  const navigate = useNavigate();

  // Auto-generate crumbs from the current URL if not provided
  const trail: Crumb[] = crumbs ?? (() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const auto: Crumb[] = [];
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      const label = SEGMENT_LABELS[segment];
      if (label) {
        auto.push({ label, href: '/' + segments.slice(0, i + 1).join('/') });
      }
    }
    return auto;
  })();

  // Derive current page label from URL if not provided
  const currentLabel = current ?? (() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return SEGMENT_LABELS[last] || last;
  })();

  return (
    <nav className="flex items-center gap-1.5 text-sm mb-4 min-w-0">
      <button
        onClick={() => {
          // If there's browser history, go back. Otherwise fall back to the
          // nearest parent route so we don't navigate outside the app.
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            const fallback = trail.length > 0 ? trail[trail.length - 1].href : '/';
            navigate(fallback, { replace: true });
          }
        }}
        className="flex-shrink-0 p-1 -ml-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        aria-label="Go back"
      >
        <ArrowLeft size={16} />
      </button>
      {/* Mobile: show parent label next to arrow (where back goes) */}
      {trail.length > 0 && (
        <span className="sm:hidden text-gray-500 dark:text-gray-400">
          {trail[trail.length - 1].label}
        </span>
      )}
      {/* Desktop: full trail + current page */}
      {trail.map((crumb, i) => (
        <span key={i} className="hidden sm:flex items-center gap-1.5 min-w-0">
          <Link
            to={crumb.href}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors truncate"
          >
            {crumb.label}
          </Link>
          <CaretRight size={12} className="flex-shrink-0 text-gray-300 dark:text-gray-600" />
        </span>
      ))}
      <span className="hidden sm:inline text-gray-800 dark:text-gray-100 font-medium line-clamp-1">
        {currentLabel}
      </span>
    </nav>
  );
}
